/**
 * Profile Assistant Knowledge Base
 * 
 * Comprehensive knowledge about the Profile/Settings page
 * for the Profile Assistant to help users with onboarding and configuration.
 * 
 * Updated: January 2026 - New sidebar-based navigation layout
 */

export const PROFILE_PAGE_LAYOUT = {
  description: 'The Profile page has a modern sidebar-based layout with three main areas.',
  structure: {
    leftSidebar: {
      description: 'Navigation sidebar on the left with categorized menu items',
      width: '280px',
      sections: ['Account', 'Integrations', 'Widget', 'Forms', 'Settings'],
      elements: {
        profileCard: 'Shows your avatar, business name, and email at the top',
        navSections: 'Grouped navigation items organized by category'
      }
    },
    mainContent: {
      description: 'Central content area that displays the selected section',
      elements: {
        header: 'Shows the current section title and description',
        body: 'Contains the section-specific forms and settings',
        stickyFooter: 'Contains the "Save Changes" button'
      }
    },
    rightToolbar: {
      description: 'Right side toolbar with Profile Assistant toggle',
      elements: {
        assistantButton: 'Opens this Profile Assistant panel'
      }
    }
  }
};

export const PROFILE_SECTIONS = {
  // ============================================
  // ACCOUNT CATEGORY
  // ============================================
  profile: {
    name: 'Profile',
    icon: '👤',
    category: 'Account',
    description: 'View and manage your account profile - your display name, profile image, and firm configuration.',
    location: 'Account → Profile in the left sidebar',
    elements: {
      accountProfile: {
        description: 'Card showing your avatar, name, email, and API status',
        fields: ['Display Name', 'Profile Image URL']
      },
      firmConfiguration: {
        description: 'Link to Firm Standards page for configuring policies and procedures',
        button: 'Firm Standards button (green) opens the configuration page'
      }
    },
    tips: [
      'Your profile picture comes from your Auth0 account',
      'The "API Linked" badge indicates your widget is ready to use',
      'Click "Firm Standards" to configure your firm\'s policies'
    ]
  },

  business: {
    name: 'Business Info',
    icon: '🏢',
    category: 'Account',
    description: 'Configure your business details including name, description, location, and category.',
    location: 'Account → Business Info in the left sidebar',
    fields: {
      businessName: 'Your company or business name',
      about: 'A description of your business',
      greetingsMessage: 'The welcome message users see in the widget',
      location: 'Your business location/address',
      website: 'Your business website URL',
      hourClose: 'Business hours closing time',
      responseTime: 'Expected response time (e.g., "4 hours")',
      industry: 'Your business category (select from dropdown)'
    },
    tips: [
      'Business name appears in the widget header',
      'The greetings message is the first thing users see',
      'Industry selection helps personalize AI responses'
    ]
  },

  contact: {
    name: 'Contact',
    icon: '📧',
    category: 'Account',
    description: 'Manage your contact information that users can use to reach you.',
    location: 'Account → Contact in the left sidebar',
    fields: {
      email: 'Your contact email address',
      phone: 'Your contact phone number',
      position: 'Your role/position in the company (e.g., CEO, Consultant)'
    }
  },

  social: {
    name: 'Social Media',
    icon: '📱',
    category: 'Account',
    description: 'Link your social media accounts to display on your profile.',
    location: 'Account → Social Media in the left sidebar',
    fields: {
      linkedin: 'Your LinkedIn profile URL',
      facebook: 'Your Facebook page URL',
      twitter: 'Your X (Twitter) profile URL',
      instagram: 'Your Instagram profile URL'
    },
    tips: [
      'Add full URLs including https://',
      'Social links can be shown in your widget'
    ]
  },

  // ============================================
  // INTEGRATIONS CATEGORY
  // ============================================
  api: {
    name: 'API Key',
    icon: '🔑',
    category: 'Integrations',
    description: 'Your unique API key that connects your widget to your account.',
    location: 'Integrations → API Key in the left sidebar',
    fields: {
      apiKey: 'Your unique identifier for the widget integration (read-only)'
    },
    actions: {
      copy: 'Click "Copy" button to copy your API key to clipboard'
    },
    important: [
      'Keep your API key private and secure',
      'If compromised, contact support to rotate your key',
      'You need an API key for the widget to work on your website'
    ],
    tips: [
      'If no API key shows, complete onboarding or contact support',
      'The API key is auto-generated during onboarding'
    ]
  },

  crm: {
    name: 'CRM Integration',
    icon: '📊',
    category: 'Integrations',
    description: 'Connect your CRM system (like Monday.com) to sync contacts and data.',
    location: 'Integrations → CRM in the left sidebar',
    purpose: 'Integrate with popular CRM platforms to automatically sync user data and conversations.',
    tips: [
      'CRM integration helps you manage leads automatically',
      'Monday.com integration is available'
    ]
  },

  calendar: {
    name: 'Calendar Integration',
    icon: '📅',
    category: 'Integrations',
    description: 'Connect your Google Calendar for scheduling appointments.',
    location: 'Integrations → Calendar in the left sidebar',
    purpose: 'Allow users to book appointments directly through the widget.',
    actions: {
      connect: 'Click the connect button and authorize access to your Google Calendar'
    },
    tips: [
      'Enables appointment booking through the widget',
      'Syncs availability from your Google Calendar'
    ]
  },

  ai: {
    name: 'AI Model Configuration',
    icon: '🤖',
    category: 'Integrations',
    description: 'Configure AI model settings for different tasks like Widget Agent, Observer, Summarizer.',
    location: 'Integrations → AI Model in the left sidebar',
    purpose: 'Fine-tune AI behavior for different assistant types.',
    elements: {
      taskSelector: 'Dropdown to select which AI task to configure',
      modelSettings: 'Settings like model type, temperature, max tokens'
    },
    tasks: [
      'Widget Agent - The customer-facing chatbot',
      'Observer - Monitors conversations and provides suggestions',
      'Summarizer - Creates conversation summaries',
      'Case Analysis - Analyzes immigration cases',
      'Workspace Assistant - Helps experts in the workspace',
      'Profile Assistant - Helps with profile settings (this assistant!)'
    ],
    tips: [
      'Each task type can have different AI settings',
      'Lower temperature = more focused responses',
      'Higher temperature = more creative responses'
    ]
  },

  // ============================================
  // WIDGET CATEGORY
  // ============================================
  'widget-access': {
    name: 'Share Widget',
    icon: '🔗',
    category: 'Widget',
    description: 'Get your shareable widget URL for direct access.',
    location: 'Widget → Share Widget in the left sidebar',
    fields: {
      shareableUrl: 'A direct link to your widget that you can share with users'
    },
    actions: {
      copy: 'Click "Copy" button to copy the widget URL',
      share: 'Click "Share" button to use your system\'s share feature',
      openLink: 'Click "Open your widget link" to see your widget live'
    },
    tips: [
      'Share this URL with clients for direct widget access',
      'Use it to test your widget configuration',
      'URL only works if you have an API key'
    ]
  },

  'widget-embed': {
    name: 'Embed Code',
    icon: '💻',
    category: 'Widget',
    description: 'Get the code snippet to add the widget to your website.',
    location: 'Widget → Embed Code in the left sidebar',
    elements: {
      embedCode: 'The HTML/JavaScript code to paste on your website',
      poweredByLink: 'The last line is a small "Powered by iMaven" link to the adviser\'s public page on imavenai.com (or imavenai.com while they are not listed in the marketplace). It is optional: the adviser may delete that line and the widget works the same',
      copyButton: 'Copies the embed code',
      tutorialButton: 'Opens a detailed integration tutorial'
    },
    actions: {
      copyCode: 'Click "Copy Embed Code" button to copy',
      viewTutorial: 'Click "View Tutorial" for step-by-step instructions'
    },
    placement: 'Paste the code before </body> tag on every page',
    platforms: [
      'Webflow - Project Settings → Custom Code → Before </body>',
      'WordPress - Use "Insert Headers and Footers" plugin or edit footer.php',
      'Shopify - Themes → Edit Code → theme.liquid → before </body>',
      'Squarespace - Settings → Advanced → Code Injection → Footer',
      'Custom HTML - Add before closing </body> tag'
    ],
    tips: [
      'Add to your global footer for site-wide coverage',
      'Test on a staging page first',
      'Check browser console for errors if widget doesn\'t appear'
    ]
  },

  'widget-style': {
    name: 'Widget Customization',
    icon: '🎨',
    category: 'Widget',
    description: 'Customize the appearance of your widget - colors and logo.',
    location: 'Widget → Customization in the left sidebar',
    fields: {
      colorScheme: {
        description: 'The color theme of your widget',
        options: ['cyan', 'darkblue', 'lightblue', 'red', 'orange', 'magenta', 'green', 'yellow']
      },
      logoUrl: 'Custom logo URL for your widget'
    },
    tips: [
      'Choose colors that match your brand',
      'lightblue is the default color scheme',
      'Logo should be a square image for best results'
    ]
  },

  // ============================================
  // FORMS CATEGORY
  // ============================================
  templates: {
    name: 'Form Templates',
    icon: '📄',
    category: 'Forms',
    description: 'Manage your PDF form templates for document automation.',
    location: 'Forms → Templates in the left sidebar',
    purpose: 'Upload and manage fillable PDF templates for automatic form filling.',
    actions: {
      viewDetails: 'Click on a template to view/edit its details'
    }
  },

  ircc: {
    name: 'IRCC Forms',
    icon: '🍁',
    category: 'Forms',
    description: 'Manage IRCC (Immigration, Refugees and Citizenship Canada) form URLs and sync.',
    location: 'Forms → IRCC Forms in the left sidebar',
    purpose: 'Specifically for Canadian immigration forms management.',
    badge: '🍁 Canadian immigration forms'
  },

  // ============================================
  // SETTINGS CATEGORY
  // ============================================
  team: {
    name: 'Team Management',
    icon: '👥',
    category: 'Settings',
    description: 'Manage your team members - invite, remove, and set roles.',
    location: 'Settings → Team in the left sidebar',
    purpose: 'Add team members who can access and manage conversations.',
    features: [
      'Invite team members via email',
      'Set roles (Admin, Member, Viewer)',
      'Remove team members',
      'Manage permissions'
    ]
  },

  meetings: {
    name: 'Meeting Settings',
    icon: '📅',
    category: 'Settings',
    description: 'Configure how users can schedule meetings with you.',
    location: 'Settings → Meetings in the left sidebar',
    fields: {
      allowMeetingsFromWidget: {
        description: 'Toggle to allow/disable meeting scheduling from the widget',
        note: 'When enabled, users can schedule meetings after conversations'
      },
      allowManualMeetings: {
        description: 'Toggle to allow/disable manual meeting scheduling',
        note: 'When enabled, users can manually schedule meetings'
      }
    }
  },

  payments: {
    name: 'Payment Settings',
    icon: '💳',
    category: 'Settings',
    description: 'Configure how you receive payments from clients. Money flows directly to you - iMaven facilitates the connection.',
    location: 'Settings → Payments in the left sidebar',
    fields: {
      masterToggle: 'Enable/disable accepting payments through iMaven',
      paypal: 'Accept payments via PayPal - enter your PayPal email or PayPal.me username',
      bit: 'Accept payments via Bit (Israeli mobile payment app) - enter your Bit phone number',
      bankTransfer: 'Accept payments via bank transfer - enter bank details',
      customLink: 'Use your own payment page (Stripe, Square, etc.)',
      defaultCurrency: 'Default currency for payment requests (USD, EUR, GBP, ILS, CAD, AUD)',
      defaultExpirationDays: 'How long before payment requests expire (3, 7, 14, or 30 days)',
      sendReminders: 'Toggle to enable automatic payment reminder emails'
    },
    important: [
      'Enable at least one payment method before you can send payment requests',
      'iMaven does NOT take a commission - money goes directly to you',
      'Bank details are only shown to clients when they need to pay'
    ],
    tips: [
      'Set up PayPal for quick international payments',
      'Use Bit for Israeli clients',
      'Bank transfer is good for large payments'
    ]
  },

  reminders: {
    name: 'Deadline Reminders',
    icon: '⏰',
    category: 'Settings',
    description: 'Configure email reminder preferences for deadlines.',
    location: 'Settings → Reminders in the left sidebar',
    purpose: 'Set up automatic email reminders for approaching deadlines.'
  },

  preferences: {
    name: 'Preferences',
    icon: '⚙️',
    category: 'Settings',
    description: 'Configure your language and timezone settings.',
    location: 'Settings → Preferences in the left sidebar',
    fields: {
      language: 'Select your preferred language for the interface',
      timezone: 'Your timezone for scheduling and timestamps'
    },
    tips: [
      'Language setting affects the interface language',
      'Timezone is auto-detected but can be changed manually'
    ]
  }
};

export const NAVIGATION_CATEGORIES = [
  {
    title: 'Account',
    description: 'Manage your account profile and business information',
    sections: ['profile', 'business', 'contact', 'social']
  },
  {
    title: 'Integrations',
    description: 'Connect external services and configure AI',
    sections: ['api', 'crm', 'calendar', 'ai']
  },
  {
    title: 'Widget',
    description: 'Share, embed, and customize your AI widget',
    sections: ['widget-access', 'widget-embed', 'widget-style']
  },
  {
    title: 'Forms',
    description: 'Manage form templates and IRCC forms',
    sections: ['templates', 'ircc']
  },
  {
    title: 'Settings',
    description: 'Team, meetings, payments, and preferences',
    sections: ['team', 'meetings', 'payments', 'reminders', 'preferences']
  }
];

export const COMMON_PROFILE_QUESTIONS = {
  // Navigation
  'How is the Profile page organized?': 'The Profile page has a left sidebar with 5 categories: Account, Integrations, Widget, Forms, and Settings. Click any item in the sidebar to view its settings in the main area. The "Save Changes" button is at the bottom.',
  'Where do I find my settings?': 'Use the left sidebar to navigate. Categories are: Account (profile, business, contact, social), Integrations (API, CRM, Calendar, AI), Widget (share, embed, style), Forms (templates, IRCC), and Settings (team, meetings, payments, reminders, preferences).',
  
  // API & Widget
  'How do I get my API key?': 'Go to Integrations → API Key in the left sidebar. Your API key is automatically generated when you complete onboarding. If you don\'t see one, contact support.',
  'How do I add the widget to my website?': 'Go to Widget → Embed Code in the left sidebar, copy the embed code, and paste it before the </body> tag on your website. Click "View Tutorial" for platform-specific instructions.',
  'How do I share my widget?': 'Go to Widget → Share Widget in the left sidebar. Copy the shareable URL or click "Open your widget link" to test it.',
  'How do I change my widget color?': 'Go to Widget → Customization in the left sidebar. Select a color from the Color Scheme dropdown.',
  
  // Integrations
  'How do I connect my calendar?': 'Go to Integrations → Calendar in the left sidebar and click the connect button to authorize access to your Google Calendar.',
  'How do I configure AI settings?': 'Go to Integrations → AI Model in the left sidebar. Select a task type and adjust parameters like temperature and max tokens.',
  
  // Business & Profile
  'How do I update my business info?': 'Go to Account → Business Info in the left sidebar. You can edit your business name, description, greeting message, location, website, and category.',
  'Where is the Firm Standards button?': 'Go to Account → Profile in the left sidebar. The "Firm Standards" green button is in the Firm Configuration card.',
  
  // Payments
  'How do I set up payments?': 'Go to Settings → Payments in the left sidebar. Enable "Accept Client Payments", then configure your payment methods (PayPal, Bit, Bank Transfer, or Custom Link).',
  'How do I request payment from a client?': 'In any conversation, click the "Request Payment" button in the toolbar. Enter details and the client will receive a payment page.',
  'Does iMaven take a cut of payments?': 'No! iMaven does not take any commission. Money goes directly to you through your configured payment method.',
  
  // Team
  'How do I add team members?': 'Go to Settings → Team in the left sidebar. You can invite team members via email and set their roles (Admin, Member, Viewer).',
  
  // General
  'How do I save my changes?': 'Click the "Save Changes" button in the sticky footer at the bottom of the main content area.',
  'How do I test my widget?': 'Go to Widget → Share Widget in the left sidebar, then click "Open your widget link" to see your widget live in a new tab.'
};

export const PROFILE_WORKFLOWS = {
  setupWidget: {
    name: 'Setting Up Your Widget',
    steps: [
      '1. Go to Integrations → API Key to ensure you have an API key',
      '2. Go to Account → Business Info and configure your business name and greeting',
      '3. Go to Widget → Customization and choose your color scheme',
      '4. Go to Widget → Embed Code and copy the code',
      '5. Paste the code on your website before </body>',
      '6. Go to Widget → Share Widget and click "Open your widget link" to test'
    ]
  },
  configureIntegrations: {
    name: 'Setting Up Integrations',
    steps: [
      '1. Go to Integrations → CRM to connect your CRM (Monday.com)',
      '2. Go to Integrations → Calendar to link your Google Calendar',
      '3. Go to Integrations → AI Model to configure AI settings for optimal responses'
    ]
  },
  customizeBranding: {
    name: 'Customizing Your Branding',
    steps: [
      '1. Go to Widget → Customization and enter your logo URL',
      '2. Select a matching color scheme',
      '3. Go to Account → Business Info and update your greeting message',
      '4. Add your business description',
      '5. Click "Save Changes"'
    ]
  },
  setupPayments: {
    name: 'Setting Up Client Payments',
    steps: [
      '1. Go to Settings → Payments in the left sidebar',
      '2. Enable "Accept Client Payments" toggle',
      '3. Enable at least one payment method (PayPal recommended)',
      '4. For PayPal: Enter your PayPal email or PayPal.me username',
      '5. For Bank Transfer: Enter your bank details',
      '6. Set your default currency and expiration days',
      '7. Enable reminders if desired',
      '8. Click "Save Payment Settings"',
      '9. Now you can request payments in any conversation!'
    ]
  },
  inviteTeamMember: {
    name: 'Inviting a Team Member',
    steps: [
      '1. Go to Settings → Team in the left sidebar',
      '2. Click "Invite Member" or similar button',
      '3. Enter the team member\'s email',
      '4. Select their role (Admin, Member, or Viewer)',
      '5. Send the invitation',
      '6. Team member receives an email to set up their account'
    ]
  },
  configureMeetings: {
    name: 'Setting Up Meeting Options',
    steps: [
      '1. Go to Integrations → Calendar and connect your Google Calendar',
      '2. Go to Settings → Meetings to configure meeting options',
      '3. Enable "Allow Meetings from Widget" to let users book after conversations',
      '4. Enable "Allow Manual Meeting Scheduling" for direct booking',
      '5. Click "Save Changes"'
    ]
  }
};

/**
 * Get contextual help based on the current section
 */
export const getProfileContextualHelp = (currentSection) => {
  const suggestions = [];
  const section = PROFILE_SECTIONS[currentSection];
  
  if (section) {
    suggestions.push(`Tell me about ${section.name}`);
    if (section.fields) {
      const fieldCount = Object.keys(section.fields).length;
      if (fieldCount > 0) {
        suggestions.push(`What fields can I configure in ${section.name}?`);
      }
    }
    if (section.tips && section.tips.length > 0) {
      suggestions.push(`Any tips for ${section.name}?`);
    }
  }
  
  // Add navigation-related suggestions
  suggestions.push('How is the Profile page organized?');
  suggestions.push('How do I set up my widget?');
  
  return suggestions.slice(0, 4);
};

/**
 * Find which section a topic belongs to
 */
export const findSectionByTopic = (topic) => {
  const lowerTopic = topic.toLowerCase();
  
  for (const [key, section] of Object.entries(PROFILE_SECTIONS)) {
    if (section.name.toLowerCase().includes(lowerTopic)) {
      return { key, section, category: section.category };
    }
  }
  
  // Search in fields
  for (const [key, section] of Object.entries(PROFILE_SECTIONS)) {
    if (section.fields) {
      for (const fieldKey of Object.keys(section.fields)) {
        if (fieldKey.toLowerCase().includes(lowerTopic)) {
          return { key, section, category: section.category, matchedField: fieldKey };
        }
      }
    }
  }
  
  return null;
};

export default {
  PROFILE_PAGE_LAYOUT,
  PROFILE_SECTIONS,
  NAVIGATION_CATEGORIES,
  COMMON_PROFILE_QUESTIONS,
  PROFILE_WORKFLOWS,
  getProfileContextualHelp,
  findSectionByTopic
};
